"use client";

import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useAppSelector, useAppDispatch } from '@/redux/hooks';
import { closeModal } from '@/redux/slices/modalSlice';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

export const GlobalModal = () => {
    const { isOpen, type, title, message, confirmLabel, cancelLabel, isConfirming } = useAppSelector((state) => state.modal);
    const dispatch = useAppDispatch();
    const cancelButtonRef = useRef(null);

    const handleConfirm = () => {
        dispatch(closeModal(true));
    };

    const handleCancel = () => {
        dispatch(closeModal(false));
    };

    const getIcon = () => {
        switch (type) {
            case 'success': return <CheckCircle2 className="text-emerald-500" size={24} />;
            case 'error': return <AlertCircle className="text-red-500" size={24} />;
            case 'warning': return <AlertTriangle className="text-amber-500" size={24} />;
            case 'confirm': return <AlertTriangle className="text-blue-500" size={24} />;
            default: return <Info className="text-blue-500" size={24} />;
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-1000" initialFocus={cancelButtonRef} onClose={() => !isConfirming && handleCancel()}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden bg-white dark:bg-zinc-900 text-left transition-all sm:my-8 sm:w-full sm:max-w-lg border border-zinc-200 dark:border-zinc-800 rounded-none">
                                <div className="bg-white dark:bg-zinc-900 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center bg-zinc-100 dark:bg-zinc-800 rounded-none sm:mx-0 sm:h-10 sm:w-10">
                                            {getIcon()}
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title as="h3" className="text-base font-black uppercase tracking-widest text-zinc-900 dark:text-zinc-100">
                                                {title || (type === 'confirm' ? '请确认' : '提示')}
                                            </Dialog.Title>
                                            <div className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 font-medium">
                                                {message}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-zinc-50 dark:bg-zinc-900/50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2 border-t border-zinc-100 dark:border-zinc-800">
                                    <button
                                        type="button"
                                        className={`inline-flex w-full justify-center px-6 py-2.5 text-xs font-black uppercase tracking-widest transition-all sm:ml-3 sm:w-auto rounded-none border outline-none
                                            ${type === 'error' ? 'bg-red-600 border-red-500 text-white hover:bg-red-500'
                                                : type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white hover:bg-emerald-500'
                                                    : 'bg-blue-600 border-blue-500 text-white hover:bg-blue-500'}`}
                                        onClick={handleConfirm}
                                    >
                                        {confirmLabel}
                                    </button>
                                    {isConfirming && (
                                        <button
                                            type="button"
                                            className="mt-3 inline-flex w-full justify-center px-6 py-2.5 text-xs font-black uppercase tracking-widest text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-700 sm:mt-0 sm:w-auto rounded-none outline-none transition-all"
                                            onClick={handleCancel}
                                            ref={cancelButtonRef}
                                        >
                                            {cancelLabel}
                                        </button>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
